/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2023-05-21 19:39:55
 */
import React, { useMemo, useCallback } from 'react'
import { Dropdown, Button } from 'antd'
import type { MenuProps } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  CaretDownOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'

type MenuItem = Required<MenuProps>['items'][number]

interface YearMonthPickerProps {
  calendarDate: Dayjs
  onCalendarDateChange: (date: Dayjs) => void
}

const createYearsMenuItems = (): MenuItem[] => {
  const years = dayjs().diff(dayjs('2016-01-01'), 'years')
  const menuItems: MenuItem[] = []
  for (let index = 0; index <= years; index++) {
    menuItems.push({
      key: String(2016 + index),
      label: String(2016 + index)
    })
  }
  return menuItems
}

const createMonthsMenuItems = (): MenuItem[] => {
  // 0 到 11
  const menuItems: MenuItem[] = []
  for (let index = 0; index <= 11; index++) {
    menuItems.push({
      key: String(index),
      label: index > 8 ? String(index + 1) : '0' + (index + 1)
    })
  }
  return menuItems
}

const YearMonthPicker: React.FC<YearMonthPickerProps> = (props) => {
  const { calendarDate, onCalendarDateChange } = props
  const year = calendarDate.year()
  const month = calendarDate.month()
  const yearsMenuProps: MenuProps = useMemo(
    () => ({
      items: createYearsMenuItems(),
      style: { textAlign: 'center' as const },
      selectedKeys: [String(year)],
      onClick: ({ key }) =>
        onCalendarDateChange(dayjs(calendarDate).year(Number(key)))
    }),
    [year, calendarDate, onCalendarDateChange]
  )

  const monthsMenuProps: MenuProps = useMemo(
    () => ({
      items: createMonthsMenuItems(),
      style: { textAlign: 'center' as const },
      selectedKeys: [String(month)],
      onClick: ({ key }) =>
        onCalendarDateChange(dayjs(calendarDate).month(Number(key)))
    }),
    [month, calendarDate, onCalendarDateChange]
  )

  const onPrevMonth = useCallback(() => {
    onCalendarDateChange(dayjs(calendarDate).add(-1, 'months'))
  }, [calendarDate, onCalendarDateChange])

  const onNextMonth = useCallback(() => {
    onCalendarDateChange(dayjs(calendarDate).add(1, 'months'))
  }, [calendarDate, onCalendarDateChange])

  return (
    <div className="year-month-picker">
      <Button
        type="link"
        size="small"
        style={{ marginRight: '8px', color: '#222222' }}
        onClick={onPrevMonth}
      >
        <LeftOutlined />
      </Button>
      <Dropdown menu={yearsMenuProps}>
        <Button className="btn-year" size="small">
          {year}年<CaretDownOutlined />
        </Button>
      </Dropdown>
      <Dropdown menu={monthsMenuProps}>
        <Button className="btn-month" size="small">
          {month > 8 ? month + 1 : '0' + (month + 1)}月
          <CaretDownOutlined />
        </Button>
      </Dropdown>
      <Button
        type="link"
        size="small"
        style={{ marginLeft: '8px', color: '#222222' }}
        onClick={onNextMonth}
      >
        <RightOutlined />
      </Button>
    </div>
  )
}

export default YearMonthPicker
